/**
 * Address checks for CCD intake forms. Each address is entered in parts, so the
 * checks only ask what the parts alone cannot enforce: does the street line have
 * a number and a name, and does the ZIP belong to the chosen state?
 *
 * These checks only warn. Rural yards and new construction often aren't in any
 * postal database, so an address that looks odd may still be right, and the
 * visitor can always continue. Nothing here rewrites a value.
 *
 * apps/api/intake/address.go is the server half and must give the same answer
 * for every case in addressCases.json; both test suites run that file.
 */
import usAddressData from './usAddressData.json'

export const poBoxRegex = /p\.?\s*o\.?\s*box/i

/** A US address in parts: state is the two-letter code, zip is 5 digits or ZIP+4. */
export interface AddressParts {
  street?: string | undefined
  unit?: string | undefined
  city?: string | undefined
  state?: string | undefined
  zip?: string | undefined
}

interface Region {
  code: string
  name: string
  zip: RegExp
}

const regionsByCode = new Map<string, Region>(
  usAddressData.regions.map((region) => [
    region.code,
    { code: region.code, name: region.name, zip: new RegExp(`^(?:${region.zip})`) },
  ]),
)

const directionals = new Set([
  'n',
  's',
  'e',
  'w',
  'ne',
  'nw',
  'se',
  'sw',
  'north',
  'south',
  'east',
  'west',
  'northeast',
  'northwest',
  'southeast',
  'southwest',
])
const ruralRoute = /^(r\.?\s?r\.?|rural route|h\.?\s?c\.?|highway contract|star route)\s*\d/i
const zipPart = /^(\d{5})(?:-\d{4})?$/

function word(token: string | undefined) {
  return (token ?? '').replace(/\./g, '').toLowerCase()
}

/** Collapses whitespace, including pasted Unicode spaces, and trims. */
function part(value: string | undefined) {
  return (value ?? '').replace(/\s+/g, ' ').trim()
}

function hasHouseNumber(street: string): boolean {
  if (poBoxRegex.test(street)) return /\d/.test(street)
  if (ruralRoute.test(street)) return true
  const [first, second] = street.split(' ')
  if (/^\d/.test(first ?? '')) return true
  if (/^[nsew]\d/i.test(first ?? '')) return true
  return directionals.has(word(first)) && /^\d/.test(second ?? '')
}

function hasStreetName(street: string): boolean {
  if (poBoxRegex.test(street) || ruralRoute.test(street)) return true
  return /[a-z]/i.test(street.split(' ').slice(1).join(' '))
}

function list(items: string[]) {
  if (items.length <= 2) return items.join(' and ')
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/** A warning when the street line looks incomplete or the ZIP is outside its state, else undefined. */
export function addressWarning(address: AddressParts | undefined): string | undefined {
  if (!address) return undefined
  const sentences: string[] = []
  const street = part(address.street)
  if (street) {
    const missing: string[] = []
    if (!hasHouseNumber(street)) missing.push('street number')
    if (!hasStreetName(street)) missing.push('street name')
    if (missing.length)
      sentences.push(`The street address looks like it's missing the ${list(missing)}.`)
  }
  const region = regionsByCode.get(part(address.state).toUpperCase())
  const zip = zipPart.exec(part(address.zip))?.[1]
  if (region && zip !== undefined && !region.zip.test(zip))
    sentences.push(`ZIP code ${zip} isn't in ${region.name}.`)
  if (!sentences.length) return undefined
  return `${sentences.join(' ')} Check it, or continue if it's right.`
}

/** Whether any part of the address has been entered. */
export function addressEntered(address: AddressParts | undefined): boolean {
  return Boolean(
    address &&
    part(
      `${address.street ?? ''}${address.unit ?? ''}${address.city ?? ''}${address.state ?? ''}${address.zip ?? ''}`,
    ),
  )
}

const abbreviations: Record<string, string> = {
  street: 'st',
  avenue: 'ave',
  av: 'ave',
  road: 'rd',
  drive: 'dr',
  lane: 'ln',
  boulevard: 'blvd',
  court: 'ct',
  place: 'pl',
  highway: 'hwy',
  parkway: 'pkwy',
  circle: 'cir',
  trail: 'trl',
  terrace: 'ter',
  square: 'sq',
  route: 'rte',
  freeway: 'fwy',
  alley: 'aly',
  suite: 'ste',
  apartment: 'apt',
  building: 'bldg',
  floor: 'fl',
  room: 'rm',
  north: 'n',
  south: 's',
  east: 'e',
  west: 'w',
  northeast: 'ne',
  northwest: 'nw',
  southeast: 'se',
  southwest: 'sw',
}

/** A comparison key: case, punctuation, common abbreviations, and ZIP+4 ignored. */
export function addressKey(address: AddressParts): string {
  const zip = part(address.zip)
  const text = [
    address.street,
    address.unit,
    address.city,
    address.state,
    zipPart.exec(zip)?.[1] ?? zip,
  ]
    .join(' ')
    .toLowerCase()
    .replace(/[.,#]/g, ' ')
  return part(text)
    .split(' ')
    .map((token) => abbreviations[token] ?? token)
    .join(' ')
}

/** Whether two addresses name the same place, allowing for formatting differences. */
export function sameAddress(a: AddressParts, b: AddressParts): boolean {
  return addressKey(a) === addressKey(b)
}
